import { PluralPricingSchemas, PricingSchemas } from 'src/app/models/enums';
import { Component, ElementRef, Input, ViewChild, OnInit, ViewEncapsulation } from '@angular/core';
import { BasketItemOption } from 'src/app/models/product-order.model';
import { DialogService } from 'src/app/services/dialog.service';
import Utils from 'src/app/shared/utils';
import { ContinueDialogResult } from '../continue-dialog/continue-dialog.component';
import { DateRangePickerComponent, DateRangePickerValue } from '../date-range-picker/date-range-picker.component';
import { Moment } from 'moment';
import { thumborBaseUrl } from 'src/app/config/app.config';
import { ProductOptionModel } from 'src/app/models/product-option.model';
import { ProductModel } from 'src/app/models/product.model';
import { ProductTypeId } from 'src/app/models/enums/product-type.enum';
import { NavigationService } from 'src/app/services/navigation.service';
import { ArrayUtils } from 'src/app/shared/array-utils';
import { AvailabilityModel, AvailabilityProvider } from 'src/app/providers/availability.provider';
import { ProductItemService, HotelPriceCalculations } from './product-item.service';
import { CartService } from 'src/app/services/cart/cart.service';
import { SoloModeProvider } from 'src/app/providers/solo-mode.provider';
import { PricingScheme } from 'src/app/models/pricing-schema.model';

@Component({
  selector: 'app-product-item',
  templateUrl: './product-item.component.html',
  styleUrls: ['./product-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProductItemComponent implements OnInit {
  pricingSchemas = PricingSchemas;
  pluralPricingSchemas = PluralPricingSchemas;

  @Input() option: ProductOptionModel;
  @Input() languages;

  @ViewChild('bookingFlow') bookingFlow: ElementRef;
  @ViewChild(DateRangePickerComponent) datepicker: DateRangePickerComponent;

  product: ProductModel;
  selectedRoomQuantity = 1;
  wineryBookingUiExpanded = false;

  hotelBookingPeriod: DateRangePickerValue;
  availabilities: Record<string, AvailabilityModel> = {}; // The key is date in API_DAY_FORMAT
  selectedAvailability: AvailabilityModel;

  availablePricingSchemas: PricingScheme[] = [];

  thumborBaseUrl = thumborBaseUrl;

  isSoloMode: boolean;

  constructor(
    private navigationService: NavigationService,
    private productItemService: ProductItemService,
    private elementRef: ElementRef<HTMLElement>,
    private cartService: CartService,
    private dialogService: DialogService,
    private soloModeProvider: SoloModeProvider,
    private availabilityProvider: AvailabilityProvider
  ) {}

  ngOnInit(): void {
    this.isSoloMode = this.soloModeProvider.isSoloMode;
    this.product = this.option.product;
  }

  get isTour() {
    return this.product.productTypeId === ProductTypeId.tour;
  }

  public get isHotel() {
    return this.product.productTypeId === ProductTypeId.hotel;
  }

  public get isEvent() {
    return this.product.productTypeId === ProductTypeId.event;
  }

  getAvailabilityForPeriod = async (start: Moment, end: Moment) => {
    const { availabilities } = await this.availabilityProvider.loadAvailability(
      start.toDate(),
      end.toDate(),
      this.option.id,
      this.product.currency
    );

    availabilities.forEach((a) => {
      this.availabilities[a.day] = a;
    });

    return availabilities;
  };

  onDatepickerChange(period: DateRangePickerValue): void {
    // We want to reset room quantity when changing the selected date
    this.selectedRoomQuantity = 1;

    this.hotelBookingPeriod = period;

    const hotelAvailabilityForPeriod =
      this.hotelBookingPeriod && this.availabilities
        ? this.productItemService.hotelAvailabilityForPeriod(this.hotelBookingPeriod, this.availabilities)
        : [];

    if (hotelAvailabilityForPeriod.length) {
      this.selectOptionAvailability(hotelAvailabilityForPeriod);
    }
  }

  expandBookingFlow() {
    this.wineryBookingUiExpanded = !this.wineryBookingUiExpanded;

    if (this.wineryBookingUiExpanded) {
      setTimeout(() => {
        // The set timeout is needed so that after setting
        // wineryBookingUiExpanded to true, we let Angular update the
        // DOM, and then do scrollIntoView, because before the DOM
        // update, bookingFlow is not expanded and is basically 0px, and
        // so centering it vertically makes no sense.
        this.bookingFlow.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }

  public isAddButtonEnabled() {
    return this.selectedAvailabilities().every((av) => this.cartService.remainingCapacity(av) > 0);
  }

  addToCart() {
    const cartItem = this.buildCartItem();

    this.cartService.addOption(cartItem);
    this.askCheckoutOrContinueBooking();
  }

  private selectedAvailabilities(): AvailabilityModel[] {
    return this.hotelBookingPeriod && this.availabilities
      ? this.productItemService.hotelAvailabilityForPeriod(this.hotelBookingPeriod, this.availabilities)
      : [];
  }

  selectOptionAvailability(hotelAvailabilityForPeriod: AvailabilityModel[]) {
    // We are taking the first availability, because every availability has the same `Pricing Schemas`
    const availability = hotelAvailabilityForPeriod[0];

    this.selectedAvailability = availability;

    this.setAvailablePricingSchemas(availability);
  }

  private setAvailablePricingSchemas(availability: AvailabilityModel) {
    this.availablePricingSchemas = this.uniquePricingSchemas(availability).map((av: PricingScheme) => {
      av.count = av.minimumCapacity;
      av.description = this.getPricingSchemaDescription(av);
      return av;
    });

    this.option.pricingSchemas = this.availablePricingSchemas;
  }

  getPricingSchemaDescription(schema: PricingScheme): string {
    return this.option.pricingSchemas?.find((x) => x.pricingSchemaId == schema.pricingSchemaId)?.description;
  }

  increaseCount(scheme: PricingScheme): void {
    scheme.count = scheme.count + 1;

    this.updateSelectedRoomQuantity(scheme);
  }

  decreaseCount(scheme: PricingScheme): void {
    scheme.count = scheme.count - 1;

    this.updateSelectedRoomQuantity(scheme);
  }

  updateSelectedRoomQuantity(scheme: PricingScheme) {
    if (scheme.pricingSchemaId === PricingSchemas.Adult) {
      const numberOfNecessaryRooms = Math.ceil(scheme.count / scheme.maximumCapacity);
      this.selectedRoomQuantity = numberOfNecessaryRooms;
    }

    const childPricingScheme = this.availablePricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Child);

    if (!childPricingScheme) return;

    if (childPricingScheme.count > childPricingScheme.maximumCapacity * this.selectedRoomQuantity) {
      childPricingScheme.count = childPricingScheme.maximumCapacity * this.selectedRoomQuantity;
    }
  }

  increaseRoomsCount() {
    if (this.selectedRoomQuantity < this.hotelRoomQuantityOptions.length) {
      const adultPricingScheme = this.availablePricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Adult);

      adultPricingScheme.count = adultPricingScheme.maximumCapacity * this.selectedRoomQuantity + 1;

      this.selectedRoomQuantity++;
    }
  }

  decreaseRoomsCount() {
    if (this.selectedRoomQuantity > 1) {
      this.selectedRoomQuantity--;

      const adultPricingScheme = this.availablePricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Adult);
      const childPricingScheme = this.availablePricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Child);

      adultPricingScheme.count = adultPricingScheme.maximumCapacity * this.selectedRoomQuantity;

      if (!childPricingScheme) return;

      if (childPricingScheme.count > childPricingScheme.maximumCapacity * this.selectedRoomQuantity)
        childPricingScheme.count = childPricingScheme.maximumCapacity * this.selectedRoomQuantity;
    }
  }

  private uniquePricingSchemas(x: AvailabilityModel) {
    return x.pricingSchemas.reduce((acc, schema) => {
      const existingSchema = acc.find((s) => s.name === schema.name);

      if (existingSchema) {
        existingSchema.minimumCapacity = Math.min(existingSchema.minimumCapacity, schema.minimumCapacity);
        existingSchema.maximumCapacity = Math.max(existingSchema.maximumCapacity, schema.maximumCapacity);

        return acc;
      }
      return acc.concat([{ ...schema }]);
    }, [] as PricingScheme[]);
  }

  pricingSchemaRate(scheme: PricingScheme): number {
    scheme.rate = this.productItemService.pricingSchemaRate(scheme, this.selectedAvailability);

    return scheme.rate;
  }

  get getExtraBeds() {
    const extraBedsPricingScheme = this.option.pricingSchemas?.find(
      (x) => x.pricingSchemaId === PricingSchemas.ExtraBed
    );

    if (!extraBedsPricingScheme) return;

    return extraBedsPricingScheme;
  }

  private buildCartItem(): BasketItemOption {
    return this.productItemService.buildCartItem(
      this.hotelBookingPeriod,
      this.option,
      this.availabilities,
      this.selectedRoomQuantity,
      this.selectedAvailability
    );
  }

  private async askCheckoutOrContinueBooking() {
    await this.scrollIntoView();

    this.dialogService
      .openContinueDialog(this.elementRef.nativeElement)
      .afterClosed()
      .subscribe((result?: ContinueDialogResult) => {
        if (result && result.navigateToCart) {
          this.navigationService.goToCart();
        }
      });
  }

  private async scrollIntoView(): Promise<void> {
    const element = this.elementRef.nativeElement;

    element.scrollIntoView({ behavior: 'smooth' });

    // We need to wait some, until the smooth scrolling ends.
    await Utils.sleep(300);
  }

  // TODO: Move to the service and add unit test
  getLowestPrice(): number {
    if (this.product.productTypeId === ProductTypeId.hotel && this.hotelBookingPeriod) {
      return this.productItemService.getLowestPriceForPeriod(this.hotelBookingPeriod, this.availabilities);
    } else {
      return this.option.lowestAvailabilityPrice;
    }
  }

  getTotalGuestsCount() {
    return this.availablePricingSchemas
      ?.filter((x) => x.pricingSchemaId != PricingSchemas.ExtraBed)
      .reduce((acc, x) => acc + x.count, 0);
  }

  getSubtotal() {
    return this.priceCalculations.total / this.priceCalculations.numberOfNights;
  }

  get priceCalculations(): HotelPriceCalculations {
    return this.productItemService.hotelPriceCalculations(
      this.hotelBookingPeriod,
      this.availabilities,
      this.selectedRoomQuantity,
      this.availablePricingSchemas,
      this.selectedAvailability
    );
  }

  get hotelRoomQuantityOptions(): number[] {
    const minRoom = this.productItemService.getMinRoomQuantityForPeriod(this.hotelBookingPeriod, this.availabilities);

    return ArrayUtils.rangeFromOneTo(minRoom);
  }

  isDecreaseCountDisabled(scheme: PricingScheme): boolean {
    return scheme.count === scheme.minimumCapacity;
  }

  isIncreaseCountDisabled(scheme: PricingScheme): boolean {
    if (scheme.pricingSchemaId === PricingSchemas.Adult) {
      return (
        scheme.count === scheme.availableCapacity * this.hotelRoomQuantityOptions.length ||
        scheme.count === scheme.maximumCapacity * this.hotelRoomQuantityOptions.length
      );
    }

    if (scheme.pricingSchemaId === PricingSchemas.Child) {
      return (
        scheme.count === scheme.availableCapacity * this.selectedRoomQuantity ||
        scheme.count === scheme.maximumCapacity * this.selectedRoomQuantity
      );
    }

    return scheme.count === scheme.availableCapacity || scheme.count === scheme.maximumCapacity;
  }

  isDecreaseRoomsCountDisabled() {
    return this.selectedRoomQuantity <= 1;
  }

  isIncreaseRoomsCountDisabled() {
    return this.selectedRoomQuantity >= this.hotelRoomQuantityOptions.length;
  }
}
